import { useState } from 'react';
import api from '../services/api';
import SEO from '../components/SEO';

const LoginPage = ({ onLogin, switchToRegister, switchToForgot, onGuestAccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [resendStatus, setResendStatus] = useState(''); // '', 'loading', 'success', 'error'
  const [resendMessage, setResendMessage] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setUnverifiedEmail('');
    setResendStatus('');
    setResendMessage('');
    setLoading(true);
    try {
      const response = await api.post('/auth/login', { email, password });
      localStorage.setItem('studymate_token', response.data.token);
      localStorage.setItem('studymate_user', JSON.stringify(response.data.user));
      onLogin(response.data.user);
    } catch (err) {
      const rawError = err.response?.data?.error;
      const parsedError = typeof rawError === 'object' 
        ? (rawError.message || rawError.code || JSON.stringify(rawError)) 
        : (rawError || 'Login failed');
      setError(parsedError);
      if (err.response?.data?.notVerified) {
        setUnverifiedEmail(err.response.data.email || email);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendStatus('loading');
    setResendMessage('');
    try {
      const response = await api.post('/auth/resend-verification', { email: unverifiedEmail });
      setResendStatus('success');
      const rawMsg = response.data.message;
      const parsedMsg = typeof rawMsg === 'object'
        ? (rawMsg.message || rawMsg.code || JSON.stringify(rawMsg))
        : (rawMsg || 'Verification email sent!');
      setResendMessage(parsedMsg);
      setError(''); // Clear main error on success
    } catch (err) {
      setResendStatus('error');
      const rawError = err.response?.data?.error;
      const parsedError = typeof rawError === 'object'
        ? (rawError.message || rawError.code || JSON.stringify(rawError))
        : (rawError || 'Failed to resend verification email.');
      setResendMessage(parsedError);
    }
  };

  return (
    <div className="auth-page">
      <SEO title="Login" description="Sign in to your Study Centro account to access your documents and exams." />
      <div className="auth-card">
        <div className="auth-header">
          <div className="top-menu-logo" style={{ margin: '0 auto 20px', width: '48px', height: '48px' }}>
            <i className="fas fa-graduation-cap" />
          </div>
          <h2>Welcome Back</h2>
        </div>
        
        <form onSubmit={handleSubmit} style={{ textAlign: 'left' }}>
          <div style={{ display: 'grid', gap: '8px' }}>
            <label>Email Address</label>
            <input 
              value={email} 
              onChange={e => {
                setEmail(e.target.value);
                setError('');
                setUnverifiedEmail('');
                setResendStatus('');
                setResendMessage('');
              }} 
              type="email" 
              required 
              placeholder="name@college.edu"
              autoFocus
            />
          </div>
          <div style={{ display: 'grid', gap: '8px', marginTop: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Password</label>
              <button type="button" className="link-button" onClick={switchToForgot} style={{ fontSize: '0.8rem' }}>
                Forgot?
              </button>
            </div>
            <input 
              value={password} 
              onChange={e => {
                setPassword(e.target.value);
                setError('');
                setUnverifiedEmail('');
                setResendStatus('');
                setResendMessage('');
              }} 
              type="password" 
              required 
              placeholder="••••••••"
            />
          </div>
          
          {error && (
            <div className="error-message" style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div>{error}</div>
              {unverifiedEmail && (
                <button
                  type="button"
                  className="link-button"
                  onClick={handleResend}
                  disabled={resendStatus === 'loading'}
                  style={{
                    fontSize: '0.85rem',
                    color: 'var(--primary)',
                    alignSelf: 'flex-start',
                    textDecoration: 'underline',
                    fontWeight: '600',
                    marginTop: '4px',
                    padding: 0
                  }}
                >
                  {resendStatus === 'loading' ? 'Resending Link...' : 'Resend verification link?'}
                </button>
              )}
            </div>
          )}
          
          {resendMessage && (
            <div 
              className={resendStatus === 'success' ? 'status-message' : 'error-message'} 
              style={{ marginTop: '12px' }}
            >
              {resendMessage}
            </div>
          )}

          <button type="submit" disabled={loading} style={{ marginTop: '20px', width: '100%', padding: '14px' }}>
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>
        
        <p style={{ marginTop: '32px', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
          Don't have an account? <button type="button" className="link-button" onClick={switchToRegister}>Create one now</button>
        </p>

        <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid var(--border-light)' }}>
          <button type="button" className="secondary" style={{ width: '100%' }} onClick={onGuestAccess}>
            Continue as Guest
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
